/**
 * Sentry initialization — graceful no-op if REACT_APP_SENTRY_DSN is missing.
 * Keep dependencies dynamic so the bundle size only grows when Sentry is enabled.
 */
let sentryEnabled = false;

export async function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  try {
    const Sentry = await import('@sentry/react');
    const { BrowserTracing } = await import('@sentry/tracing');

    Sentry.init({
      dsn,
      environment: process.env.NODE_ENV || 'development',
      release: process.env.REACT_APP_RELEASE || 'nexus@1.0.0',
      integrations: [new BrowserTracing()],
      tracesSampleRate: parseFloat(process.env.REACT_APP_SENTRY_TRACES_SAMPLE || '0.1'),
      // Don't send events for these noisy/uncontrollable error sources
      ignoreErrors: [
        'ResizeObserver loop',
        'Network request failed',
        'Failed to fetch', // covered by our own friendly UI fallback
        'Load failed',
        'NetworkError',
        'AbortError',
        // Browser extensions
        /^chrome-extension:/,
        /^moz-extension:/,
      ],
      beforeSend(event, hint) {
        const error = hint.originalException;
        // Drop events that have no useful stack trace
        if (error && typeof error === 'object' && 'message' in error) {
          const msg = String(error.message || '');
          if (msg.includes('cancelled') || msg.includes('canceled')) return null;
        }
        return event;
      },
    });
    sentryEnabled = true;
    return true;
  } catch (e) {
    console.warn('[sentry] init failed (continuing without it)', e);
    return false;
  }
}

export function captureException(error, context = {}) {
  if (!sentryEnabled) return;
  import('@sentry/react').then((Sentry) => {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
      Sentry.captureException(error);
    });
  });
}

export function isSentryEnabled() {
  return sentryEnabled;
}
